import React, {
  ClipboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  components,
  MultiValue,
  MultiValueProps,
  SelectInstance,
} from 'react-select';
import { useLazyQuery } from '@apollo/client';
import { CreatableReactSelectMenu } from '../../../../../../Dropdown/StyledReactSelect';
import {
  IFieldFieldsFragment,
  IGetFieldValuesQuery,
  IGetFieldValuesQueryVariables,
  PermissionKey,
} from '../../../../../../../utils/apollo/graphqlSchema';
import { BasicEntity } from '../../../../../../EntityLabels';
import { GET_FIELD_VALUES } from '../../../../../../../pages/Formulas/apollo';
import { colors } from '../../../../../../../config/styles';
import { useFilterWidgetPropContext } from '../../../../../FilterWidgetPropContext';
import usePermission from '../../../../../../../utils/hooks/permissionHooks/usePermission';
import { createRefreshableMenuComponent } from '../../../../../../DataRefresh/RefreshableMenuList';
import { getFieldValueKey } from '../../../../../../../utils/analytics/events/filter/utils';
import { IOperandValue } from '../../../../../types';
import { isCreatableReactSelectOption } from '../../../../../../Builder/EntitySelection/reactSelectUtils';
import { createBasicFieldValue } from '../../../../../utils/createBasicFieldValue';

interface IProps {
  operands: IOperandValue[];
  selectedField: IFieldFieldsFragment;
  // As an output we allow:
  // 1. Any value only (if includeAnyValue is true)
  // 2. A list of Basic values
  onChange: (newValues: IOperandValue[]) => void;
  placeholder: string;
  autoFocus?: boolean;
  disabled?: boolean;
  hasError: boolean;
  includeAnyValue?: boolean;
}

const CreatableOperandMenu = ({
  selectedField,
  operands,
  onChange,
  placeholder,
  autoFocus,
  disabled,
  hasError,
  includeAnyValue = false,
}: IProps) => {
  const ref = useRef<SelectInstance<IOperandValue, true>>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { schemaId, context } = useFilterWidgetPropContext();

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadFields = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FieldRead],
    schemaId,
  });

  const [loadFieldValues, { data, loading }] = useLazyQuery<
    IGetFieldValuesQuery,
    IGetFieldValuesQueryVariables
  >(GET_FIELD_VALUES, {
    variables: {
      fieldKey: selectedField.key,
      parents: [],
      term: searchTerm,
      schemaId,
      includeValues: operands.map((operand) => operand.name),
      events: context.events,
      includeAny: includeAnyValue,
    },
  });

  const fieldValues = data?.fieldValues;

  const handleLoadOptions = useCallback(() => {
    if (isPermittedToReadFields) {
      loadFieldValues();
    }
  }, [loadFieldValues, isPermittedToReadFields]);

  const handleChange = (selectedOptions: MultiValue<IOperandValue>) => {
    // Potential selectedOptions array values (A - Any FV, B - Basic FV):
    // [A] - only Any
    // [A, B] - was A, user select B -> result should be B
    // [B, A] - was B only, user select A -> result should be A
    // [B, B] - was B only, user select B -> result should include both
    // * A would be included only when includeAnyValue=true

    if (!includeAnyValue || selectedOptions.length === 1) {
      onChange([...selectedOptions]);
      return;
    }

    const lastOption = selectedOptions[selectedOptions.length - 1];

    if (lastOption.__typename === 'AnyFieldValue') {
      onChange([lastOption]);
      return;
    }

    onChange(
      selectedOptions.filter((option) => option.__typename !== 'AnyFieldValue'),
    );
  };

  const handleRemoveValue = (index: number) => {
    onChange(operands.filter((_, arrayIndex) => arrayIndex !== index));
  };

  const renderMultiValue = (props: MultiValueProps<IOperandValue, true>) => {
    return (
      <components.MultiValueLabel {...props}>
        <BasicEntity onClear={() => handleRemoveValue(props.index)}>
          {props.children}
        </BasicEntity>
      </components.MultiValueLabel>
    );
  };

  useEffect(() => {
    if (!disabled && autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus, disabled]);

  const RefreshableMenu = useMemo(
    () =>
      createRefreshableMenuComponent<IOperandValue, true>({
        field: selectedField,
        schemaId,
        onRefreshEnd: handleLoadOptions,
      }),
    [schemaId, selectedField, handleLoadOptions],
  );

  const onCreateOption = (inputValue: string) => {
    const operandsList = operands.filter(
      (item) => item.__typename !== 'AnyFieldValue',
    );
    onChange([...operandsList, createBasicFieldValue(inputValue)]);
  };

  const handlePasteValues: ClipboardEventHandler<HTMLInputElement> = (
    event,
  ) => {
    event.preventDefault();
    const clipboardData = event.clipboardData.getData('text');

    // We expect to receive a coma/semicolon separated list that
    // user wants to add as a list of operands
    if (clipboardData.length > 0) {
      const values = clipboardData
        .trim()
        // try to split list by comma or semicolon
        .split(/[,;\s]+/)
        // Filter-out already selected operands
        .filter((value) => !operands.some((operand) => operand.name === value));

      if (ref.current && values.length > 0) {
        onChange([
          ...operands.filter(
            (operand) => operand.__typename !== 'AnyFieldValue',
          ),
          ...values.map((clipboardValue) => {
            // Check field values first
            if (fieldValues?.length) {
              const fieldValue = fieldValues.find(
                (fieldValue) =>
                  fieldValue.__typename === 'BasicFieldValue' &&
                  fieldValue.key === clipboardValue,
              );

              if (fieldValue) {
                return fieldValue;
              }
            }

            // Create a new Basic field for all custom values pasted in input
            return createBasicFieldValue(clipboardValue);
          }),
        ]);

        ref.current?.blur();
      }
    }
  };

  return (
    <div onPaste={handlePasteValues}>
      <CreatableReactSelectMenu<IOperandValue, true>
        ref={ref}
        value={operands}
        getOptionLabel={(option) =>
          isCreatableReactSelectOption(option) ? option.label : option.name
        }
        getOptionValue={(option) => {
          return isCreatableReactSelectOption(option)
            ? option.value
            : getFieldValueKey(option);
        }}
        isLoading={loading}
        onChange={handleChange}
        placeholder={placeholder}
        inputValue={searchTerm}
        onMenuOpen={handleLoadOptions}
        options={fieldValues || []}
        onInputChange={(newValue, actionMeta) => {
          if (actionMeta.action === 'input-change') {
            setSearchTerm(newValue);
          }
        }}
        onMenuClose={() => setSearchTerm('')}
        onCreateOption={onCreateOption}
        blurInputOnSelect
        openMenuOnFocus
        isMulti={true}
        isClearable
        isDisabled={disabled}
        components={{
          MultiValue: renderMultiValue,
          Menu: RefreshableMenu,
        }}
        styles={{
          control: (base) => {
            const result = {
              ...base,
              minWidth: '100%',
              maxWidth: '100%',
            };

            if (hasError) {
              result.borderColor = colors.mainError;
            }

            return result;
          },
          multiValue: (base) => ({
            ...base,
            backgroundColor: 'unset',
          }),
        }}
        menuPortalTarget={document.body}
      />
    </div>
  );
};

export default CreatableOperandMenu;
